import { ArrowLeftIcon, PlusIcon } from "~/assets/icons";
import styles from "./CRMDetails.module.scss";
import classNames from "classnames/bind";
import { useEffect, useState } from "react";
import ActivityTab from "./tabs/activity/ActivityTab";
import JobTab from "./tabs/job/JobTab";
import DocumentTab from "./tabs/document/DocumentTab";
import FinancialTab from "./tabs/financial/FinancialTab";
import { useNavigate } from "react-router-dom";
import UploadFileModal from "./tabs/document/add-new-lead/UploadFile";
import EditCustomerModal from "../edit-customer/EditCustomerModal";
import AddNoteModal from "./AddNoteModal";
import SendEmailModal from "./SendEmailModal";
import AddNewMessage from "~/pages/admin/communication/add-new-message/AddNewMessage";
import {
  handleGetClientDetails,
  handleUploadFile,
  handleAddNote,
  handleGetActivities,
} from "~/thunks/crm/crmThunk";
import { notify } from "~/utils/helper";
import { useAppDispatch, useAppSelector } from "~/redux/hooks";
import { crmActions } from "~/thunks/crm/crmSlice";
const cx = classNames.bind(styles);

const tabs = [
  { key: "activity", label: "Activity" },
  { key: "job", label: "Jobs" },
  // { key: "documents", label: "Documents" },
  // { key: "financials", label: "Financials" },
];

const CRMDetails = () => {
  const idClient = window.location.pathname.split("/").pop() || "";
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(tabs[0].key);
  const [tabTransition, setTabTransition] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showAddNoteModal, setShowAddNoteModal] = useState(false);
  const [showSendEmailModal, setShowSendEmailModal] = useState(false);
  const [showSendMessageModal, setShowSendMessageModal] = useState(false);
  const { client, needRefreshData } = useAppSelector((state) => state.crm);

  useEffect(() => {
    if (needRefreshData || dispatch) {
      dispatch(handleGetClientDetails({ idClient: idClient }))
        .unwrap()
        .catch((error) => {
          notify("error", `Failed to fetch client: ${error}`);
        });
    }
  }, [needRefreshData, dispatch, idClient]);

  const handleTabChange = (key: string) => {
    if (key !== activeTab) {
      setTabTransition(true);
      setTimeout(() => {
        setActiveTab(key);
        setTabTransition(false);
      }, 200);
    }
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case "activity":
        return <ActivityTab />;
      case "job":
        return <JobTab />;
      case "documents":
        return (
          <DocumentTab onOpenUploadFileModal={handleOpenUploadFileModal} />
        );
      case "financials":
        return <FinancialTab />;
      default:
        return null;
    }
  };

  const handleButtonBackClick = () => {
    navigate(`/admin/crm`);
  };

  const handleOpenUploadFileModal = () => {
    setShowUploadModal(true);
  };

  const handleCancelUploadFile = () => {
    setShowUploadModal(false);
  };

  const handleSaveUploadFile = async (data: any) => {
    try {
      await dispatch(
        handleUploadFile({ idClient: idClient, data: data })
      ).unwrap();
      notify("success", "Upload successful!");
      setShowUploadModal(false);
      dispatch(crmActions.changeNeedRefreshData(true));
    } catch (error) {
      notify("error", "Upload failed!");
    }
  };

  return (
    <div className={cx("crm-details-container")}>
      <div className={cx("header")}>
        <div className={cx("header-info")}>
          <button className={cx("back-button")} onClick={handleButtonBackClick}>
            <ArrowLeftIcon className={cx("back-icon")} />
          </button>
          <div className={cx("client-info")}>
            <h3 className={cx("client-name")}>{client.name}</h3>
            <p className={cx("client-id")}>Client ID: {client.clientID}</p>
          </div>
        </div>
        <div className={cx("header-actions")}>
          <div className={cx("action-buttons")}>
            <button
              className={cx("btn", "btn-send-message")}
              onClick={() => setShowSendMessageModal(true)}
            >
              <PlusIcon className={cx("btn-icon")} />
              <span className={cx("btn-label")}>Send Message</span>
            </button>

            <button 
              className={cx("btn", "btn-send-email")}
              onClick={() => setShowSendEmailModal(true)}
            >
              <PlusIcon className={cx("btn-icon")} />
              <span className={cx("btn-label")}>Send Email</span>
            </button>

            <button className={cx("btn", "btn-add-note")} onClick={() => setShowAddNoteModal(true)}>
              <PlusIcon className={cx("btn-icon")} />
              <span className={cx("btn-label")}>Add Note</span>
            </button>
          </div>
          <button className={cx("edit-button")} onClick={() => setShowEditModal(true)}>
            Edit Customer
          </button>
        </div>
      </div>

      <div className={cx("content")}>
        <div className={cx("header-content")}>
          <div className={cx("header-content-item")}>
            <h4 className={cx("item-title")}>Contact Details</h4>

            <div className={cx("item-detail", "mb-[15px]")}>
              <div className={cx("detail-block")}>
                <p className={cx("detail-title")}>Contact Number</p>
                <p className={cx("detail-info")}>{client.phoneNumber}</p>
              </div>

              <div className={cx("detail-block")}>
                <p className={cx("detail-title")}>Email</p>
                <p className={cx("detail-info")}>{client.email}</p>
              </div>
            </div>

            <div className={cx("item-detail")}>
              <div className={cx("detail-block")}>
                <p className={cx("detail-title")}>Address</p>
                <p className={cx("detail-info")}>{client.address}</p>
              </div>
            </div>
          </div>
          <div className={cx("header-content-item")}>
            <h4 className={cx("item-title")}>Workflow</h4>

            <div className={cx("item-detail", "mb-[15px]")}>
              <div className={cx("detail-block")}>
                <p className={cx("detail-title")}>Status</p>
                <p className={cx("detail-info")}>Active</p>
              </div>

              <div className={cx("detail-block")}>
                <p className={cx("detail-title")}>Text</p>
                <p className={cx("detail-info")}>{client.textOptInStatus || "---"}</p>
              </div>
            </div>

            <div className={cx("item-detail")}>
              <div className={cx("detail-block")}>
                <p className={cx("detail-title")}>Assigned To</p>
                <p className={cx("detail-info")}>{client.assignedTo?.name || "---"}</p>
              </div>

              <div className={cx("detail-block")}>
                <p className={cx("detail-title")}>Created By</p>
                <p className={cx("detail-info")}>{client.createdBy?.name || "---"}</p>
              </div>
            </div>
          </div>
        </div>
        <div className={cx("tab-bar")}>
          {tabs.map((tab) => (
            <button
              key={tab.key}
              className={cx("tab-item", { active: activeTab === tab.key })}
              onClick={() => handleTabChange(tab.key)}
            >
              <h4 className={cx("tab-text")}>{tab.label}</h4>
            </button>
          ))}
        </div>

        <div
          className={cx("tab-content", { "tab-content--fade": tabTransition })}
        >
          {renderTabContent()}
        </div>
      </div>
      {showUploadModal && (
        <UploadFileModal
          isOpen={showUploadModal}
          onCancel={handleCancelUploadFile}
          onSave={handleSaveUploadFile}
        />
      )}
      {showEditModal && (
        <EditCustomerModal
          isOpen={showEditModal}
          onCancel={() => setShowEditModal(false)}
          onSubmit={() => {
            dispatch(crmActions.changeNeedRefreshData(true));
            setShowEditModal(false);
          }}
          customer={client}
        />
      )}
      {showAddNoteModal && (
        <AddNoteModal
          isOpen={showAddNoteModal}
          onCancel={() => setShowAddNoteModal(false)}
          onSave={async (note) => {
            try {
              await dispatch(handleAddNote({ customerId: idClient, content: note })).unwrap();
              notify("success", "Note added successfully!");
              setShowAddNoteModal(false);
              dispatch(crmActions.changeNeedRefreshData(true));
              // Refresh activities if on activity tab
              if (activeTab === 'activity') {
                dispatch(handleGetActivities({
                  customerId: idClient,
                  params: {
                    page: 1,
                    limit: 10,
                    sortBy: "createdAt",
                    sortType: "DESC"
                  }
                }));
              }
            } catch (error) {
              notify("error", "Failed to add note!");
            }
          }}
        />
      )}
      {showSendEmailModal && (
        <SendEmailModal
          isOpen={showSendEmailModal}
          onCancel={() => setShowSendEmailModal(false)}
          onSuccess={() => {
            dispatch(crmActions.changeNeedRefreshData(true));
            // Refresh activities if on activity tab
            if (activeTab === 'activity') {
              dispatch(handleGetActivities({
                customerId: idClient,
                params: {
                  page: 1,
                  limit: 10,
                  sortBy: "createdAt",
                  sortType: "DESC"
                }
              }));
            }
          }}
          customerEmail={client.email || undefined}
          customerName={client.name}
        />
      )}
      <AddNewMessage
        open={showSendMessageModal}
        onClose={() => setShowSendMessageModal(false)}
        defaultContactPhone={client.phoneNumber || undefined}
      />
    </div>
  );
};

export default CRMDetails;